import { z } from 'zod';

/**
 * The Honeypot Theater contract.
 *
 * An `AttackEvent` is one observed probe against the honeypot surface, already
 * sanitized for public display (IPs are truncated to a /24, never raw). The
 * dashboard renders a live stream of these plus rolling aggregates; both shapes
 * are defined here and shared by the Worker and the SPA.
 */

/** Rule-based bucket a probe is sorted into. Drives colours + the feed label. */
export const classificationSchema = z.enum([
  'wordpress', // /wp-login.php, /wp-admin, xmlrpc
  'credential-stuffing', // a fake login form was POSTed with creds
  'env-leak', // /.env, /config.php, secrets files
  'git-exposure', // /.git/*, /.svn/*
  'admin-probe', // /admin, /phpmyadmin, /administrator
  'cve-probe', // path matches a known CVE fingerprint
  'path-traversal', // ../ / %2e%2e sequences
  'shell-upload', // webshell / rce upload attempts
  'iot-botnet', // router/NAS/DVR default-cred style paths
  'proxy-abuse', // absolute-URI / CONNECT proxy probing
  'scanner', // generic scanner fingerprint
  'recon', // everything else worth logging
]);
export type Classification = z.infer<typeof classificationSchema>;

/** A single observed probe, sanitized for public display. */
export const attackEventSchema = z.object({
  /** Monotonic id assigned by the EventHub (SQLite rowid). */
  id: z.number().int(),
  /** Epoch milliseconds when the probe hit the trap. */
  ts: z.number().int(),
  /** Source IP truncated to a /24 for display, e.g. `185.220.101.x`. */
  ipDisplay: z.string(),
  /** ISO-3166 alpha-2 country code from `request.cf`, or null if unknown. */
  country: z.string().nullable(),
  /** City from `request.cf`, or null. */
  city: z.string().nullable(),
  /** Latitude from `request.cf`, or null. */
  lat: z.number().nullable(),
  /** Longitude from `request.cf`, or null. */
  lon: z.number().nullable(),
  /** Autonomous System number, or null. */
  asn: z.number().int().nullable(),
  /** AS organization name (e.g. `DIGITALOCEAN-ASN`), or null. */
  asOrg: z.string().nullable(),
  /** The Cloudflare colo (data centre) that served the request. */
  colo: z.string().nullable(),
  /** HTTP method. */
  method: z.string(),
  /** Request path (no query string). */
  path: z.string(),
  /** Query string without the leading `?`, or null. */
  query: z.string().nullable(),
  /** User-Agent header, truncated, or null. */
  userAgent: z.string().nullable(),
  /** Username submitted to a fake login form, or null. */
  username: z.string().nullable(),
  /** Password submitted to a fake login form, or null. */
  password: z.string().nullable(),
  /** Classifier bucket. */
  classification: classificationSchema,
  /** Short human label for the feed, e.g. `WordPress login probe`. */
  label: z.string(),
});
export type AttackEvent = z.infer<typeof attackEventSchema>;

/** One entry in a leaderboard (top usernames, passwords, paths, ...). */
export const countItemSchema = z.object({
  value: z.string(),
  count: z.number().int().min(0),
});
export type CountItem = z.infer<typeof countItemSchema>;

/**
 * One distinct username/password pair and how often it was tried — the Hall of
 * Shame's data. Server-aggregated over the ring buffer so counts survive page
 * reloads instead of resetting with the client's small event buffer.
 */
export const credentialPairSchema = z.object({
  username: z.string().nullable(),
  password: z.string().nullable(),
  count: z.number().int().min(1),
});
export type CredentialPair = z.infer<typeof credentialPairSchema>;

/** Rolling aggregates maintained by the EventHub. */
export const statsSchema = z.object({
  /** All-time attacks seen since the ring buffer's counters were last reset. */
  totalAttacks: z.number().int().min(0),
  /**
   * All-time requests the Worker has handled — trapped probes plus everything
   * served as the dashboard or its API. Always >= totalAttacks; the gap is
   * traffic that hit an allowlisted path (a bot scanning `/`, or a real viewer)
   * and so was never classified as an attack.
   */
  totalRequests: z.number().int().min(0),
  /** Attacks in the last 60 seconds. */
  attacksPerMin: z.number().int().min(0),
  /** Epoch ms of the first event the hub ever recorded. */
  firstSeen: z.number().int().nullable(),
  topUsernames: z.array(countItemSchema),
  topPasswords: z.array(countItemSchema),
  topPaths: z.array(countItemSchema),
  topCountries: z.array(countItemSchema),
  topClassifications: z.array(countItemSchema),
  /** Distinct credential pairs by attempt count (Hall of Shame). */
  topCredentials: z.array(credentialPairSchema),
});
export type Stats = z.infer<typeof statsSchema>;

/**
 * Messages the EventHub pushes over the WebSocket. `snapshot` is sent once on
 * connect (so a lone viewer sees instant activity); `event` and `stats` stream
 * thereafter.
 */
export const serverMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('snapshot'),
    events: z.array(attackEventSchema),
    stats: statsSchema,
  }),
  z.object({
    type: z.literal('event'),
    event: attackEventSchema,
  }),
  z.object({
    type: z.literal('stats'),
    stats: statsSchema,
  }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

/** Payload of `GET /api/recent` — the same data as the WS `snapshot`. */
export const recentResponseSchema = z.object({
  events: z.array(attackEventSchema),
  stats: statsSchema,
});
export type RecentResponse = z.infer<typeof recentResponseSchema>;

/**
 * One plotted origin on the map's historical view: a coordinate cell with a
 * count of attacks that came from it and the classification that dominates it
 * (drives the node's colour). Coordinates are rounded server-side so nearby
 * probes cluster into a single node sized by how busy the origin is.
 */
export const mapPointSchema = z.object({
  lat: z.number(),
  lon: z.number(),
  count: z.number().int().min(1),
  classification: classificationSchema,
});
export type MapPoint = z.infer<typeof mapPointSchema>;

/**
 * Payload of `GET /api/history` — aggregated attack origins across the whole
 * ring buffer, powering the map's "all-time" view (vs. the live 60 pins).
 */
export const historyResponseSchema = z.object({
  points: z.array(mapPointSchema),
  /** Total geolocated attacks represented by `points`. */
  total: z.number().int().min(0),
});
export type HistoryResponse = z.infer<typeof historyResponseSchema>;
